import React, { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Alert,
} from "@mui/material";
import GoogleIcon from "@mui/icons-material/Google";
import LayersIcon from "@mui/icons-material/Layers";

import {
  useAppDispatch,
  useAppSelector,
  loginWithGoogle,
  clearAuthError,
} from "../store";

const API_BASE_URL = process.env.API_URL || "/api";
const REDIRECT_URI = window.location.origin + "/login";

const LoginPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const codeProcessed = useRef(false);

  const { isAuthenticated, isLoading, error } = useAppSelector(
    (state) => state.auth,
  );

  // Handle OAuth callback - backend returns code, we send it back to exchange
  useEffect(() => {
    const code = searchParams.get("code");
    if (code && !isLoading && !codeProcessed.current && !error) {
      codeProcessed.current = true;
      dispatch(loginWithGoogle({ code, redirectUri: REDIRECT_URI }))
        .unwrap()
        .then(() => {})
        .catch(() => {
          setSearchParams({}, { replace: true });
        });
    }
  }, [searchParams, dispatch, isLoading, error, setSearchParams]);

  // Redirect if authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate("/services", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Clear error on unmount
  useEffect(() => {
    return () => {
      dispatch(clearAuthError());
    };
  }, [dispatch]);

  const handleGoogleLogin = () => {
    // Redirect to backend's Google OAuth initiation endpoint
    // The backend handles building the Google auth URL with client_id/secret
    const backendAuthUrl = `${API_BASE_URL}/auth/google/redirect?redirectUri=${encodeURIComponent(REDIRECT_URI)}`;
    window.location.href = backendAuthUrl;
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "#f9fafb",
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={0}
          sx={{
            p: 6,
            textAlign: "center",
            bgcolor: "#ffffff",
            borderRadius: 3,
            border: "1px solid",
            borderColor: "#e5e7eb",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Box sx={{ mb: 4 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.5,
                mb: 2,
              }}
            >
              <LayersIcon sx={{ color: "#10b981", fontSize: 36 }} />
              <Typography
                variant="h3"
                component="h1"
                sx={{
                  fontWeight: 700,
                  color: "text.primary",
                }}
              >
                Pinehood LTS
              </Typography>
            </Box>
            <Typography
              variant="body1"
              sx={{
                color: "text.secondary",
              }}
            >
              On-demand Docker services for developers
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {isLoading ? (
            <Box sx={{ py: 4 }}>
              <CircularProgress size={40} sx={{ color: "primary.main" }} />
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  mt: 2,
                }}
              >
                Signing you in...
              </Typography>
            </Box>
          ) : (
            <Button
              variant="contained"
              size="large"
              fullWidth
              startIcon={<GoogleIcon />}
              onClick={handleGoogleLogin}
              sx={{
                py: 1.5,
                fontSize: "1rem",
              }}
            >
              Sign in with Google
            </Button>
          )}

          <Box
            sx={{
              mt: 4,
              pt: 3,
              borderTop: "1px solid",
              borderColor: "#e5e7eb",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
              }}
            >
              Deploy PostgreSQL, Redis, Jenkins, and 40+ more services
              instantly.
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default LoginPage;
